import * as twgl from "twgl.js";
import { createBlurProgram } from "./programs";

interface BlurTarget {
  texture: WebGLTexture;
  framebuffer: WebGLFramebuffer;
  width: number;
  height: number;
}

interface GaussianKernel {
  offsets: number[];
  weights: number[];
}

/**
 * Largest Gaussian σ the separable kernel can express faithfully in one pass.
 * Larger requested radii are handled by downsampling first so document-space
 * blur stays stable across zoom (σ_device / scale ≈ σ_document).
 */
export const MAX_GAUSSIAN_BLUR_SIGMA = 48;

/**
 * Map Curva's document `blur` to the Gaussian σ used by {@link GlBlurPass}.
 *
 * SVG export writes `stdDeviation={blur}` on `feDropShadow` / `feGaussianBlur`.
 * Browser SVG filters read ~2× softer than a truncated separable kernel with
 * σ=`blur`, so the canvas looked about twice as sharp. Scaling here matches
 * SVG softness without changing the document model or export.
 */
export const DOCUMENT_BLUR_TO_GAUSSIAN_SIGMA = 2;

export function documentBlurToSigma(blurDoc: number): number {
  return Math.max(0, blurDoc) * DOCUMENT_BLUR_TO_GAUSSIAN_SIGMA;
}
const MAX_LINEAR_TAPS = 16;

/** Device-pixel padding (≥ discrete kernel support) for soft transparent edges. */
export function gaussianBlurPaddingPx(radiusPx: number): number {
  if (radiusPx <= 0) {
    return 0;
  }
  // Padding is for the texture we actually blur. Large radii downsample to
  // MAX_GAUSSIAN_BLUR_SIGMA, so pad for the effective post-downsample kernel.
  const sigma = Math.min(MAX_GAUSSIAN_BLUR_SIGMA, Math.max(0.5, radiusPx));
  return Math.ceil(sigma * 2.5) + 1;
}

/**
 * GPU separable Gaussian blur (ping-pong FBOs).
 * Matches CSS/`Canvas2D` `blur(σ)` closely enough for drop shadow / layer / backdrop.
 * Safe on Safari/WKWebView where `context.filter` is a no-op behind a flag.
 *
 * When `radiusPx` exceeds {@link MAX_GAUSSIAN_BLUR_SIGMA}, the source is
 * downsampled so the blur runs at max σ, then upsampled — keeping the
 * document-space blur radius equal to `radiusPx / sourceScale` at any zoom.
 */
export class GlBlurPass {
  private readonly program: twgl.ProgramInfo;
  private readonly quad: twgl.BufferInfo;
  private ping?: BlurTarget;
  private pong?: BlurTarget;

  constructor(private readonly gl: WebGL2RenderingContext) {
    this.program = createBlurProgram(gl);
    this.quad = twgl.createBufferInfoFromArrays(gl, {
      position: {
        numComponents: 2,
        data: [-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1],
      },
      texcoord: {
        numComponents: 2,
        data: [0, 0, 1, 0, 0, 1, 0, 1, 1, 0, 1, 1],
      },
    });
  }

  /**
   * Blur `source` by ~`radiusPx` (CSS blur sigma in texels).
   * Always returns a **new** texture the caller owns (does not delete `source`).
   * Restores the previously bound framebuffer / viewport / blend / scissor state.
   */
  blur(source: WebGLTexture, width: number, height: number, radiusPx: number): WebGLTexture {
    const gl = this.gl;
    if (width < 1 || height < 1) {
      return this.copyTexture(source, Math.max(1, width), Math.max(1, height));
    }
    if (radiusPx <= 0.01) {
      return this.copyTexture(source, width, height);
    }

    const prev = this.saveGlState();
    gl.disable(gl.BLEND);
    gl.disable(gl.SCISSOR_TEST);
    gl.useProgram(this.program.program);
    twgl.setBuffersAndAttributes(gl, this.program, this.quad);

    let result: WebGLTexture;
    if (radiusPx <= MAX_GAUSSIAN_BLUR_SIGMA) {
      result = this.blurAtSize(source, width, height, radiusPx);
    } else {
      // Downsample so requested σ maps onto MAX_GAUSSIAN_BLUR_SIGMA, blur, upsample.
      // effectiveFullResσ = MAX * (width / smallW) ≈ radiusPx.
      const factor = radiusPx / MAX_GAUSSIAN_BLUR_SIGMA;
      const smallW = Math.max(1, Math.round(width / factor));
      const smallH = Math.max(1, Math.round(height / factor));
      const smallSigma = radiusPx * (smallW / width);
      const downsampled = this.blit(source, smallW, smallH);
      const blurredSmall = this.blurAtSize(downsampled, smallW, smallH, smallSigma);
      gl.deleteTexture(downsampled);
      result = this.blit(blurredSmall, width, height);
      gl.deleteTexture(blurredSmall);
    }

    this.restoreGlState(prev);
    return result;
  }

  /** Copy `source` into a newly allocated texture (caller owns the result). */
  copyTexture(source: WebGLTexture, width: number, height: number): WebGLTexture {
    const prev = this.saveGlState();
    const gl = this.gl;
    gl.disable(gl.BLEND);
    gl.disable(gl.SCISSOR_TEST);
    gl.useProgram(this.program.program);
    twgl.setBuffersAndAttributes(gl, this.program, this.quad);
    const result = this.blit(source, width, height);
    this.restoreGlState(prev);
    return result;
  }

  dispose(): void {
    this.disposeTarget(this.ping);
    this.disposeTarget(this.pong);
    this.ping = undefined;
    this.pong = undefined;
  }

  /** Assumes program/quad bound and blend/scissor already disabled. */
  private blurAtSize(source: WebGLTexture, width: number, height: number, radiusPx: number): WebGLTexture {
    const sigma = Math.min(MAX_GAUSSIAN_BLUR_SIGMA, Math.max(0.5, radiusPx));
    const kernel = buildLinearGaussianKernel(sigma);
    this.ensureTargets(width, height);

    // Single H+V pass — extra iterations would raise effective σ by √n and make
    // the same document blur look softer whenever device σ crossed the threshold.
    this.drawPass(source, this.ping!, [1 / width, 0], kernel, { premultiplySrc: true, unpremultiplyDst: false });
    this.drawPass(this.ping!.texture, this.pong!, [0, 1 / height], kernel, { premultiplySrc: false, unpremultiplyDst: false });

    return this.resolvePremulToStraight(this.pong!.texture, width, height);
  }

  /** Resize-blit `source` into a new texture of `dstW`×`dstH` (LINEAR filter). */
  private blit(source: WebGLTexture, dstW: number, dstH: number): WebGLTexture {
    const gl = this.gl;
    const target = createBlurTarget(gl, dstW, dstH);
    const offsets = new Float32Array(MAX_LINEAR_TAPS);
    const weights = new Float32Array(MAX_LINEAR_TAPS);
    weights[0] = 1;

    gl.bindFramebuffer(gl.FRAMEBUFFER, target.framebuffer);
    gl.viewport(0, 0, dstW, dstH);
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);
    twgl.setUniforms(this.program, {
      u_texture: source,
      u_direction: [0, 0],
      u_offsets: offsets,
      u_weights: weights,
      u_tapCount: 1,
      u_premultiplySrc: 0,
      u_unpremultiplyDst: 0,
    });
    twgl.drawBufferInfo(gl, this.quad);

    const texture = target.texture;
    gl.deleteFramebuffer(target.framebuffer);
    return texture;
  }

  private resolvePremulToStraight(source: WebGLTexture, width: number, height: number): WebGLTexture {
    const gl = this.gl;
    const target = createBlurTarget(gl, width, height);
    const offsets = new Float32Array(MAX_LINEAR_TAPS);
    const weights = new Float32Array(MAX_LINEAR_TAPS);
    weights[0] = 1;

    gl.bindFramebuffer(gl.FRAMEBUFFER, target.framebuffer);
    gl.viewport(0, 0, width, height);
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);
    twgl.setUniforms(this.program, {
      u_texture: source,
      u_direction: [0, 0],
      u_offsets: offsets,
      u_weights: weights,
      u_tapCount: 1,
      u_premultiplySrc: 0,
      u_unpremultiplyDst: 1,
    });
    twgl.drawBufferInfo(gl, this.quad);

    const texture = target.texture;
    gl.deleteFramebuffer(target.framebuffer);
    return texture;
  }

  private drawPass(
    source: WebGLTexture,
    target: BlurTarget,
    direction: [number, number],
    kernel: GaussianKernel,
    alpha: { premultiplySrc: boolean; unpremultiplyDst: boolean },
  ): void {
    const gl = this.gl;
    gl.bindFramebuffer(gl.FRAMEBUFFER, target.framebuffer);
    gl.viewport(0, 0, target.width, target.height);
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);

    const offsets = new Float32Array(MAX_LINEAR_TAPS);
    const weights = new Float32Array(MAX_LINEAR_TAPS);
    for (let i = 0; i < kernel.offsets.length; i += 1) {
      offsets[i] = kernel.offsets[i]!;
      weights[i] = kernel.weights[i]!;
    }

    twgl.setUniforms(this.program, {
      u_texture: source,
      u_direction: direction,
      u_offsets: offsets,
      u_weights: weights,
      u_tapCount: kernel.offsets.length,
      u_premultiplySrc: alpha.premultiplySrc ? 1 : 0,
      u_unpremultiplyDst: alpha.unpremultiplyDst ? 1 : 0,
    });
    twgl.drawBufferInfo(gl, this.quad);
  }

  private saveGlState() {
    const gl = this.gl;
    return {
      framebuffer: gl.getParameter(gl.FRAMEBUFFER_BINDING) as WebGLFramebuffer | null,
      viewport: gl.getParameter(gl.VIEWPORT) as Int32Array,
      blend: gl.isEnabled(gl.BLEND),
      scissor: gl.isEnabled(gl.SCISSOR_TEST),
      program: gl.getParameter(gl.CURRENT_PROGRAM) as WebGLProgram | null,
      activeTexture: gl.getParameter(gl.ACTIVE_TEXTURE) as number,
      texture0: gl.getParameter(gl.TEXTURE_BINDING_2D) as WebGLTexture | null,
    };
  }

  private restoreGlState(prev: ReturnType<GlBlurPass["saveGlState"]>): void {
    const gl = this.gl;
    gl.bindFramebuffer(gl.FRAMEBUFFER, prev.framebuffer);
    gl.viewport(prev.viewport[0]!, prev.viewport[1]!, prev.viewport[2]!, prev.viewport[3]!);
    if (prev.blend) {
      gl.enable(gl.BLEND);
    } else {
      gl.disable(gl.BLEND);
    }
    if (prev.scissor) {
      gl.enable(gl.SCISSOR_TEST);
    } else {
      gl.disable(gl.SCISSOR_TEST);
    }
    gl.useProgram(prev.program);
    gl.activeTexture(prev.activeTexture);
    gl.bindTexture(gl.TEXTURE_2D, prev.texture0);
  }

  private ensureTargets(width: number, height: number): void {
    this.ping = this.ensureTarget(this.ping, width, height);
    this.pong = this.ensureTarget(this.pong, width, height);
  }

  private ensureTarget(existing: BlurTarget | undefined, width: number, height: number): BlurTarget {
    if (existing && existing.width === width && existing.height === height) {
      return existing;
    }
    this.disposeTarget(existing);
    return createBlurTarget(this.gl, width, height);
  }

  private disposeTarget(target: BlurTarget | undefined): void {
    if (!target) {
      return;
    }
    this.gl.deleteTexture(target.texture);
    this.gl.deleteFramebuffer(target.framebuffer);
  }
}

function createBlurTarget(gl: WebGL2RenderingContext, width: number, height: number): BlurTarget {
  const texture = gl.createTexture();
  const framebuffer = gl.createFramebuffer();
  if (!texture || !framebuffer) {
    if (texture) {
      gl.deleteTexture(texture);
    }
    if (framebuffer) {
      gl.deleteFramebuffer(framebuffer);
    }
    throw new Error("Failed to allocate blur target.");
  }

  gl.bindTexture(gl.TEXTURE_2D, texture);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, width, height, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);

  gl.bindFramebuffer(gl.FRAMEBUFFER, framebuffer);
  gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, texture, 0);
  if (gl.checkFramebufferStatus(gl.FRAMEBUFFER) !== gl.FRAMEBUFFER_COMPLETE) {
    gl.deleteTexture(texture);
    gl.deleteFramebuffer(framebuffer);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    throw new Error("Blur framebuffer incomplete.");
  }
  gl.bindFramebuffer(gl.FRAMEBUFFER, null);

  return { texture, framebuffer, width, height };
}

/**
 * Discrete Gaussian → linear-sample pairs (GPU Gems 3 style).
 * `weights[0]` is the center; remaining entries are half-weights for ±offset.
 * Truncated tails are renormalized so the packed taps still sum to 1.
 */
export function buildLinearGaussianKernel(sigma: number): GaussianKernel {
  const radius = Math.min(MAX_GAUSSIAN_BLUR_SIGMA, Math.max(1, Math.ceil(sigma * 2.5)));
  const discrete: number[] = [];
  let sum = 0;
  for (let i = -radius; i <= radius; i += 1) {
    const weight = Math.exp(-(i * i) / (2 * sigma * sigma));
    discrete.push(weight);
    sum += weight;
  }
  for (let i = 0; i < discrete.length; i += 1) {
    discrete[i]! /= sum;
  }

  const centerIndex = radius;
  const offsets = [0];
  const weights = [discrete[centerIndex]!];

  for (let i = 1; i <= radius; ) {
    if (offsets.length >= MAX_LINEAR_TAPS) {
      break;
    }
    const w0 = discrete[centerIndex + i]!;
    const w1 = i + 1 <= radius ? discrete[centerIndex + i + 1]! : 0;
    const w = w0 + w1;
    if (w <= 1e-8) {
      break;
    }
    const offset = w1 > 0 ? (i * w0 + (i + 1) * w1) / w : i;
    offsets.push(offset);
    weights.push(w);
    i += w1 > 0 ? 2 : 1;
  }

  let packedSum = weights[0]!;
  for (let i = 1; i < weights.length; i += 1) {
    packedSum += 2 * weights[i]!;
  }
  if (packedSum > 1e-8 && Math.abs(packedSum - 1) > 1e-5) {
    for (let i = 0; i < weights.length; i += 1) {
      weights[i]! /= packedSum;
    }
  }

  return { offsets, weights };
}
